import { useState, useCallback, useMemo } from 'react'
import { X } from 'lucide-react'
import { FileIcon } from '@papan/core-ui/ui'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { getRootFolderId } from './driveRoot'
import { PickerNavigator, readableName, type PickerCrumb, type PickerRoot, type PickerSelection } from '@papan/files/ui/picker'

interface DrivePickerDialogProps {
  tenantId: string
  /** Called with the selected file/folder ID and whether it's a folder */
  onSelect: (id: string, isFolder: boolean) => void
  onClose: () => void
  title?: string
}

/**
 * Pick a file OR folder from the drive. Single-column drilldown (issue
 * #21): folders drill in on click (the open folder becomes the selection),
 * files are selected on click. Search spans the whole permitted tree.
 */
export function DrivePickerDialog({ tenantId, onSelect, onClose, title = 'Select File or Folder' }: DrivePickerDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const rootId = getRootFolderId(tenantId)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedIsFolder, setSelectedIsFolder] = useState(false)
  const [selectedName, setSelectedName] = useState<string | null>(null)

  const roots = useMemo<PickerRoot[]>(() => [{ id: rootId, label: 'papan' }], [rootId])

  // Drilling into a folder makes it the selection target; a list place
  // (Projects, Shared) is no folder, so nothing is selected until one opens.
  const handleNavigate = useCallback((folderId: string | null, breadcrumbs: PickerCrumb[]) => {
    setSelectedId(folderId)
    setSelectedIsFolder(folderId !== null)
    setSelectedName(folderId === null ? null : breadcrumbs[breadcrumbs.length - 1]?.name ?? folderId)
  }, [])

  // Clicking a file row selects it.
  const handleSelect = useCallback((item: PickerSelection) => {
    setSelectedId(item.id)
    setSelectedIsFolder(item.isFolder)
    setSelectedName(item.name)
  }, [])

  const handleConfirm = () => {
    if (selectedId) onSelect(selectedId, selectedIsFolder)
  }

  return (
    <div data-testid="drive-picker-dialog" className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className={`${themeClass} relative themed-bg-secondary rounded-lg shadow-2xl w-[600px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[90vh] flex flex-col border themed-border`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h3 className="text-sm font-medium themed-text">{title}</h3>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {/* Navigator */}
        <PickerNavigator
          roots={roots}
          initialFolderId={rootId}
          mode="fileOrFolder"
          selectedId={selectedId}
          onSelect={handleSelect}
          onConfirm={(item) => onSelect(item.id, item.isFolder)}
          onNavigate={handleNavigate}
          className="flex-1"
        />

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t themed-border">
          <div className="text-xs themed-text-muted truncate max-w-[200px]">
            {selectedName ? (
              <span className="flex items-center gap-1">
                {selectedIsFolder
                  ? <FileIcon isFolder size={12} />
                  : <FileIcon name={selectedName} size={12} />
                }
                {readableName(selectedName)}
              </span>
            ) : 'Select a file or folder'}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedId}
              className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
